import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { X, HelpCircle, Loader2, CheckCircle2 } from "lucide-react";
import { useSupportRequest, collectDebugInfo } from "./hooks/useSupportRequest";
import { useBookingDraft } from "../booking/BookingDraftContext";
import { useTranslation } from "react-i18next";

type Status = "idle" | "sending" | "success" | "error";

interface SupportRequestProps {
    /** If true, renders only the modal (button should be rendered externally) */
    externalTrigger?: boolean;
    /** Controlled open state when using external trigger */
    isOpen?: boolean;
    /** Callback when modal closes */
    onClose?: () => void;
}

export default function SupportRequest({
    externalTrigger = false,
    isOpen: externalIsOpen,
    onClose: externalOnClose,
}: SupportRequestProps) {
    const { t } = useTranslation();
    const [internalIsOpen, setInternalIsOpen] = React.useState(false);
    const [message, setMessage] = React.useState("");
    const [status, setStatus] = React.useState<Status>("idle");
    const [errorMsg, setErrorMsg] = React.useState<string | null>(null);

    const { draft } = useBookingDraft();
    const companyId = draft.companyId ?? 0;
    const mutation = useSupportRequest();

    // Use external state if provided, otherwise internal
    const isOpen = externalTrigger ? (externalIsOpen ?? false) : internalIsOpen;
    const setIsOpen = externalTrigger
        ? (open: boolean) => { if (!open && externalOnClose) externalOnClose(); }
        : setInternalIsOpen;

    const handleSubmit = async () => {
        if (!message.trim()) return;

        setStatus("sending");
        setErrorMsg(null);

        try {
            await mutation.mutateAsync({
                company_id: companyId,
                content: message.trim(),
                extra: collectDebugInfo(),
            });
            setStatus("success");
            setMessage("");
            // Auto-close after success
            setTimeout(() => {
                setIsOpen(false);
                setStatus("idle");
            }, 2000);
        } catch (e: any) {
            setStatus("error");
            setErrorMsg(e?.response?.data?.detail || e?.message || t("widget.common.submit_error", "Could not submit"));
        }
    };

    const handleClose = () => {
        setIsOpen(false);
        setStatus("idle");
        setErrorMsg(null);
    };

    return (
        <>
            {/* Modal Overlay */}
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm">
                    <Card className="w-full sm:max-w-[420px] bg-card ios-shadow border-0 sm:rounded-2xl rounded-t-2xl overflow-hidden animate-in slide-in-from-bottom duration-300">
                        {/* Header */}
                        <div className="p-4 border-b border-border/10 flex items-center justify-between">
                            <h2 className="text-lg font-semibold">{t("widget.support.need_help", "Need help?")}</h2>
                            <Button variant="ghost" size="icon" onClick={handleClose}>
                                <X className="w-5 h-5" />
                            </Button>
                        </div>

                        {/* Body */}
                        <div className="p-6 space-y-4">
                            {status === "success" ? (
                                <div className="flex flex-col items-center gap-3 py-8">
                                    <CheckCircle2 className="w-12 h-12 text-green-500" />
                                    <p className="text-center text-muted-foreground">
                                        {t("widget.support.success", "Your message has been sent. We will contact you soon.")}
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <p className="text-sm text-muted-foreground">
                                        {t("widget.support.description", "Describe your issue or question, and we will do our best to help.")}
                                    </p>

                                    <Textarea
                                        placeholder={t("widget.support.placeholder", "Describe your issue...")}
                                        value={message}
                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value)}
                                        className="min-h-[120px] resize-none"
                                        disabled={status === "sending"}
                                    />

                                    {status === "error" && errorMsg && (
                                        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                                            {errorMsg}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Footer */}
                        {status !== "success" && (
                            <div
                                className="border-t border-border/10 bg-card/95"
                                style={{ paddingBottom: "max(env(safe-area-inset-bottom), 16px)" }}
                            >
                                <div className="p-4 pt-3">
                                    <Button
                                        className="w-full h-12 rounded-xl font-semibold"
                                        onClick={handleSubmit}
                                        disabled={status === "sending" || !message.trim()}
                                    >
                                        {status === "sending" ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                {t("widget.common.submitting", "Submitting...")}
                                            </>
                                        ) : (
                                            t("widget.common.submit", "Submit")
                                        )}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Card>
                </div>
            )}
        </>
    );
}

/** 
 * Standalone button component for triggering support modal.
 * Use this in headers where button placement is controlled externally.
 */
export function SupportRequestButton({ onClick }: { onClick: () => void }) {
    const { t } = useTranslation();

    return (
        <button
            className="flex items-center gap-2 px-3 py-2 rounded-full bg-primary/10 hover:bg-primary/20 transition-colors cursor-pointer"
            onClick={onClick}
            title={t("widget.support.need_help", "Need help?")}
        >
            <HelpCircle className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium text-primary">{t("widget.support.label", "Help")}</span>
        </button>
    );
}
